import { useEffect, useState, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";

export type NearbyUser = {
  id: string;
  name: string | null;
  avatar_url: string | null;
  lat: number;
  lng: number;
  location_updated_at: string | null;
  status: string | null;
};

export type MapBounds = { north: number; south: number; east: number; west: number };

export default function useNearbyUsers(
  currentUserId?: string | null,
  bounds?: MapBounds | null
) {
  const [users, setUsers] = useState<NearbyUser[]>([]);
  const [loading, setLoading] = useState(true);
  const channelRef = useRef<any>(null);
  const blocksChannelRef = useRef<any>(null);
  const debounceRef = useRef<number | null>(null);

  const refresh = async () => {
    // trigger immediate load (same as debounced load body)
    try {
      setLoading(true);
      let query = supabase
        .from("safe_users")
        .select("id, name, avatar_url, lat, lng, location_updated_at, status");

      if (bounds) {
        query = query
          .gte('lat', bounds.south)
          .lte('lat', bounds.north)
          .gte('lng', bounds.west)
          .lte('lng', bounds.east)
          .limit(300);
      } else {
        query = query.limit(300);
      }

      if (currentUserId) {
        query = query.not("id", "eq", currentUserId);
      }

      const { data, error } = await query;
      if (error) {
        console.error("Failed to fetch nearby users:", error);
      } else if (data) {
        setUsers(
          data.map((u: any) => ({
            id: u.id,
            name: u.name,
            avatar_url: u.avatar_url,
            lat: Number(u.lat),
            lng: Number(u.lng),
            location_updated_at: u.location_updated_at,
            status: u.status,
          }))
        );
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      await refresh();
      if (!mounted) return;
    };

    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(load, 250);

    // Subscribe to realtime changes
    try {
      const channel = supabase
        .channel("public:users")
        .on(
          "postgres_changes",
          { event: "*", schema: "public", table: "users" },
          async (payload) => {
            const evt = payload.eventType as "INSERT" | "UPDATE" | "DELETE";
            const row: any = (payload as any).new || (payload as any).old;
            if (!row) return;

            // Exclude self from realtime updates
            if (currentUserId && row.id === currentUserId) return;

            // For DELETE events, just remove the user
            if (evt === "DELETE") {
              setUsers((prev) => prev.filter((p) => p.id !== row.id));
              return;
            }

            const hasCoords = row.lat != null && row.lng != null && row.is_active;

            // If no coords or not active, remove from map
            if (!hasCoords) {
              setUsers((prev) => prev.filter((p) => p.id !== row.id));
              return;
            }

            // If we have bounds, check if out of bounds
            if (bounds) {
              const latN = Number(row.lat);
              const lngN = Number(row.lng);
              const outside = latN < bounds.south || latN > bounds.north || lngN < bounds.west || lngN > bounds.east;
              if (outside) {
                setUsers((prev) => prev.filter((p) => p.id !== row.id));
                return;
              }
            }

            // For INSERT and UPDATE events, validate against safe_users before adding
            // This ensures blocked users (in either direction) are not shown
            const { data: safeUser } = await supabase
              .from("safe_users")
              .select("id, name, avatar_url, lat, lng, location_updated_at, status")
              .eq("id", row.id)
              .maybeSingle();

            if (!safeUser) {
              // User is blocked or not visible - remove from map if present
              setUsers((prev) => prev.filter((p) => p.id !== row.id));
              return;
            }

            // User passed safe_users validation - add or update them
            setUsers((prev) => {
              const existingIndex = prev.findIndex((p) => p.id === safeUser.id);
              if (existingIndex >= 0) {
                return prev.map((p) => 
                  p.id === safeUser.id 
                    ? { 
                        ...p, 
                        lat: Number(safeUser.lat), 
                        lng: Number(safeUser.lng), 
                        name: safeUser.name, 
                        avatar_url: safeUser.avatar_url, 
                        location_updated_at: safeUser.location_updated_at, 
                        status: safeUser.status 
                      } 
                    : p
                );
              } else {
                return [
                  ...prev,
                  { 
                    id: safeUser.id!, 
                    name: safeUser.name, 
                    avatar_url: safeUser.avatar_url, 
                    lat: Number(safeUser.lat), 
                    lng: Number(safeUser.lng), 
                    location_updated_at: safeUser.location_updated_at, 
                    status: safeUser.status 
                  }
                ];
              }
            });
          }
        )
        .subscribe();

      channelRef.current = channel;
    } catch (e) {
      console.warn("Realtime subscription failed:", e);
    }

    // Subscribe to user_blocks changes for real-time block updates
    try {
      const blocksChannel = supabase
        .channel("public:user_blocks")
        .on(
          "postgres_changes",
          { event: "*", schema: "public", table: "user_blocks" },
          async (payload) => {
            const row = (payload as any).new || (payload as any).old;
            if (!row) return;

            // Check if this block involves us (as blocker or blocked)
            if (row.blocker_id === currentUserId || row.blocked_id === currentUserId) {
              // Refetch from safe_users which respects blocking
              let query = supabase
                .from("safe_users")
                .select("id, name, avatar_url, lat, lng, location_updated_at, status");

              if (bounds) {
                query = query
                  .gte('lat', bounds.south)
                  .lte('lat', bounds.north)
                  .gte('lng', bounds.west)
                  .lte('lng', bounds.east)
                  .limit(300);
              } else {
                query = query.limit(300);
              }

              if (currentUserId) {
                query = query.not("id", "eq", currentUserId);
              }

              const { data, error } = await query;
              if (!error && data) {
                setUsers(
                  data.map((u: any) => ({
                    id: u.id,
                    name: u.name,
                    avatar_url: u.avatar_url,
                    lat: Number(u.lat),
                    lng: Number(u.lng),
                    location_updated_at: u.location_updated_at,
                    status: u.status,
                  }))
                );
              }
            }
          }
        )
        .subscribe();

      blocksChannelRef.current = blocksChannel;
    } catch (e) {
      console.warn("Blocks realtime subscription failed:", e);
    }

    return () => {
      mounted = false;
      if (debounceRef.current) {
        window.clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
      if (channelRef.current) {
        try {
          supabase.removeChannel(channelRef.current);
        } catch (e) {
          try {
            channelRef.current.unsubscribe?.();
          } catch {}
        }
        channelRef.current = null;
      }
      if (blocksChannelRef.current) {
        try {
          supabase.removeChannel(blocksChannelRef.current);
        } catch (e) {
          try {
            blocksChannelRef.current.unsubscribe?.();
          } catch {}
        }
        blocksChannelRef.current = null;
      }
    };
  }, [currentUserId, bounds?.north, bounds?.south, bounds?.east, bounds?.west]);

  return { users, loading, refresh };
}

